import { Component, ElementRef, ViewChild, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, pinRefusedMessage } from '../../../auth/auth.service';
import { SessionService } from '../../../auth/session.service';

/**
 * Sign-in popup shown over the current screen when the session runs out.
 * Whatever the user was doing stays on the page underneath; the requests
 * that hit the 401 are retried once they sign in (see AuthInterceptor).
 * Only the same user can continue — "Sign out" is the way to switch users.
 */
@Component({
  selector: 'app-session-relogin',
  standalone: true,
  imports: [FormsModule],
  template: `
    @if (session.relogin(); as prompt) {
    <div class="relogin-backdrop"></div>
    <div class="modal d-block relogin-modal" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered">
        <form class="modal-content" (ngSubmit)="signIn()">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-lock me-2"></i>Sign in to continue
            </h5>
          </div>
          <div class="modal-body">
            <p class="mb-3">
              {{ prompt.reason }} Your work on this screen is kept — sign in
              again and it will carry on where it stopped.
            </p>
            <label class="form-label">Username / email</label>
            <input
              class="form-control mb-3"
              name="identifier"
              [(ngModel)]="identifier"
              [readonly]="identifierLocked"
              autocomplete="username"
            />
            <label class="form-label">Password</label>
            <input
              #secretInput
              class="form-control"
              type="password"
              name="secret"
              [(ngModel)]="secret"
              autocomplete="current-password"
            />
            @if (error) {
            <div class="text-danger small mt-2">{{ error }}</div>
            }
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" (click)="signOut()" [disabled]="busy">
              Sign out
            </button>
            <button type="submit" class="btn btn-primary" [disabled]="busy || !identifier || !secret">
              {{ busy ? 'Signing in…' : 'Sign in' }}
            </button>
          </div>
        </form>
      </div>
    </div>
    }
  `,
  styles: [
    `
      /* Above ng-bootstrap modals (1055) and the loader, so a confirm dialog
         that was open when the session ran out cannot hide it. */
      .relogin-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.55);
        z-index: 2000;
      }
      .relogin-modal {
        z-index: 2001;
      }
    `,
  ],
})
export class SessionReloginComponent {
  session = inject(SessionService);
  private authService = inject(AuthService);
  private router = inject(Router);

  @ViewChild('secretInput') secretInput?: ElementRef<HTMLInputElement>;

  identifier = '';
  identifierLocked = false;
  secret = '';
  error = '';
  busy = false;

  constructor() {
    effect(() => {
      if (!this.session.relogin()) return;
      const user = this.session.storedUser();
      this.identifier = user?.username || user?.email || '';
      this.identifierLocked = !!this.identifier;
      this.secret = '';
      this.error = '';
      setTimeout(() => this.secretInput?.nativeElement.focus());
    });
  }

  signIn(): void {
    if (this.busy || !this.identifier || !this.secret) return;
    this.busy = true;
    this.error = '';
    this.authService
      .reauthenticate({ identifier: this.identifier.trim(), secret: this.secret })
      .subscribe({
        next: () => {
          this.busy = false;
          this.secret = '';
          this.session.finishRelogin(true);
        },
        error: (err) => {
          this.busy = false;
          this.secret = '';
          this.error =
            err?.message === 'different-user'
              ? 'That is a different account. Sign in as the same user, or sign out.'
              : err?.status === 401
                ? pinRefusedMessage(err) ?? 'Wrong password.'
                : 'Could not reach the server. Check the connection and try again.';
        },
      });
  }

  signOut(): void {
    this.session.finishRelogin(false);
    this.authService.logout();
    this.router.navigate(['']);
  }
}
